/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
/**
 * This class provides an aggregation of the properties of a search filter, and factory methods to build common filters.
 * A filter may be associated to an UI component to let the user fill search filters, and may also be used directly as a
 * request payload to send search criteria to a server.
 */
class Filter {
    /**
     * Builds a filter.
     *
     * @param {Object} data Filter data that shall be of this form:
     * <pre>
     * {
     *     key: '<property key>',
     *     operator: '<operator-key>',
     *     negate: true if the filter must be reversed when sent to the server.
     *     value: <any value/object/array>,
     *     type: '<type to be used backend-side>',
     *     placeholderKey: '<placeholder key for display>',
     *     uiType: '<type of the UI component used to render the filter',
     *     options: '<array of options (with id and labelKey properties)',
     *     onChange: <callback called when the filter change>.
     *     transform: <callback called before the filter is sent in a HTTP request>.
     *     hidden: true if the filter must be hidden in the UI.
     *     required: true if the filter must be set.
     * }
     * </pre>
     */
    constructor(data) {
        Object.assign(this, data);
        this.negate = this.negate || false;
        this.hidden = this.hidden || false;
    }

    /**
     * Builds an EQUAL filter fully customizable.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'SELECT', 'NUMBER').
     * Defaults to 'LABEL'.
     * @param {Array} [options] Array of options (with 'id' and 'labelKey' properties).
     * @param {Function} [onChange] Callback called when the filter change.
     * @param {Boolean} [required] If the filter must be set.
     * @return {Filter} A filter.
     */
    static equalFilter(key, value, placeholderKey, type, uiType, options, onChange, required) {
        return new Filter({
            key,
            operator: '=',
            negate: false,
            value,
            placeholderKey,
            type,
            uiType: uiType || 'LABEL',
            options,
            onChange,
            required
        });
    }

    /**
     * Builds an EQUAL filter with a default value.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'SELECT', 'NUMBER').
     * Defaults to 'LABEL'.
     * @param {Array} [options] Array of options (with 'id' and 'labelKey' properties).
     * @param {Function} [onChange] Callback called when the filter change.
     * @return {Filter} A filter.
     */
    static equalFilterWithValue(key, value, placeholderKey, uiType, options, onChange) {
        return Filter.equalFilter(key, value, placeholderKey, undefined, uiType, options, onChange);
    }

    /**
     * Builds an empty EQUAL filter, with no value and no type.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'SELECT', 'NUMBER').
     * Defaults to 'LABEL'.
     * @param {Array} [options] Array of options (with 'id' and 'labelKey' properties).
     * @param {Function} [onChange] Callback called when the filter change.
     * @param {Boolean} [required] If the filter must be set.
     * @return {Filter} A filter.
     */
    static equalFilterEmpty(key, placeholderKey, uiType, options, onChange, required) {
        return Filter.equalFilter(key, undefined, placeholderKey, undefined, uiType, options, onChange, required);
    }

    /**
     * Builds a NOT EQUAL filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'SELECT', 'NUMBER').
     * Defaults to 'LABEL'.
     * @param {Array} options Array of options (with 'id' and 'labelKey' properties).
     * @param {Function} [onChange] Callback called when the filter change.
     * @return {Filter} A filter.
     */
    static notEqualFilter(key, value, placeholderKey, type, uiType, options, onChange) {
        return new Filter({
            key,
            operator: '=',
            negate: true,
            value,
            placeholderKey,
            type,
            uiType: uiType || 'LABEL',
            options,
            onChange
        });
    }

    /**
     * Builds an empty LIKE filter.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @return {Filter} A filter.
     */
    static emptyLikeFilter(key, placeholderKey, type) {
        return Filter.likeFilter(key, undefined, placeholderKey, type);
    }

    /**
     * Builds a LIKE filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @return {Filter} A filter.
     */
    static likeFilter(key, value, placeholderKey, type) {
        return new Filter({
            key,
            operator: '~',
            negate: false,
            value,
            placeholderKey,
            type
        });
    }

    /**
     * Builds a NOT LIKE filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @return {Filter} A filter.
     */
    static notLikeFilter(key, value, placeholderKey, type) {
        return new Filter({
            key,
            operator: '~',
            negate: true,
            value,
            placeholderKey,
            type
        });
    }

    /**
     * Builds a NOT NULL filter.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @return {Filter} A filter.
     */
    static notNullFilter(key, placeholderKey) {
        return Filter.nullFilterWithOptions(key, placeholderKey, true, Filter.yesNoOptions());
    }

    /**
     * Builds a NULL filter.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @return {Filter} A filter.
     */
    static nullFilter(key, placeholderKey) {
        return Filter.nullFilterWithOptions(key, placeholderKey, false);
    }

    /**
     * Builds a NULL/NOT-NULL/disabled filter with a select UI component.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {Boolean} [value] true if null by default, false if not-null, undefined if disabled (the filter will not be
     * sent to the server).
     * @param {Array} options Array of options (with 'id' and 'labelKey' properties). Defaults to:
     * <pre>
     * [{
     *     id: true,
     *     labelKey: 'ui.button.yes'
     * }, {
     *     id: false,
     *     labelKey: 'ui.button.no'
     * }]
     * </pre>
     * @return {Filter} A filter.
     */
    static nullFilterWithOptions(key, placeholderKey, value, options) {
        return new Filter({
            key,
            operator: 'N',
            value,
            placeholderKey,
            options: options || Filter.yesNoOptions()
        });
    }

    /**
     * Builds a GREATER OR EQUAL filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'NUMBER', 'DATE'). Defaults
     * to 'NUMBER'.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @param {Boolean} [required] If the filter must be set.
     * @return {Filter} A filter.
     */
    static greaterOrEqualFilter(key, value, placeholderKey, type, uiType, transform, hidden, required) {
        return new Filter({
            key,
            operator: '>=',
            negate: false,
            value,
            placeholderKey,
            type,
            uiType: uiType || 'NUMBER',
            transform,
            hidden,
            required
        });
    }

    /**
     * Builds a GREATER OR EQUAL filter for date.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static greaterOrEqualDateFilter(key, value, placeholderKey, transform, hidden) {
        return Filter.greaterOrEqualFilter(key, value, placeholderKey, 'DATE', 'DATE', transform, hidden);
    }

    /**
     * Builds a GREATER OR EQUAL filter for date and time.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {Boolean} [required] If the filter must be set.
     * @return {Filter} A filter.
     */
    static greaterOrEqualDateTimeFilter(key, value, placeholderKey, transform, hidden, required) {
        return Filter.greaterOrEqualFilter(key, value, placeholderKey, 'DATE_TIME', 'DATE', transform, hidden,
            required);
    }

    /**
     * Builds a LOWER filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'NUMBER', 'DATE'). Defaults
     * to 'NUMBER'.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static lowerFilter(key, value, placeholderKey, type, uiType, transform, hidden) {
        return new Filter({
            key,
            operator: '<',
            negate: false,
            value,
            placeholderKey,
            type,
            uiType: uiType || 'NUMBER',
            transform,
            hidden
        });
    }

    /**
     * Builds a LOWER filter for date.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static lowerDateFilter(key, value, placeholderKey, transform, hidden) {
        return Filter.lowerFilter(key, value, placeholderKey, 'DATE', 'DATE', transform, hidden);
    }

    /**
     * Builds a LOWER filter for date and time.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static lowerDateTimeFilter(key, value, placeholderKey, transform, hidden) {
        return Filter.lowerFilter(key, value, placeholderKey, 'DATE_TIME', 'DATE', transform, hidden);
    }

    /**
     * Builds a LOWER OR EQUAL filter.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'NUMBER', 'DATE'). Defaults
     * to 'NUMBER'.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static lowerOrEqualFilter(key, value, placeholderKey, type, uiType, transform, hidden) {
        return new Filter({
            key,
            operator: '<=',
            negate: false,
            value,
            placeholderKey,
            type,
            uiType: uiType || 'NUMBER',
            transform,
            hidden
        });
    }

    /**
     * Builds a LOWER OR EQUAL filter for date.
     *
     * @param {String} key Server-side property key.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {String} [type] Type used to decode the value server-side.
     * @param {Function} [transform] Function called before the filter is serialized in an HTTP request.
     * @return {Filter} A filter.
     */
    static lowerOrEqualDateFilter(key, value, placeholderKey, transform, hidden) {
        return Filter.lowerOrEqualFilter(key, value, placeholderKey, 'DATE', 'DATE', transform, hidden);
    }


    /**
     * Builds an EQUAL filter for a boolean property.
     *
     * @param {String} key Server-side property key.
     * @param {String} [placeholderKey] Placeholder used when the filter needs to be rendered.
     * @param {Object} [value] Value used as the second operand.
     * @param {String} [uiType] Type of the UI component used to render the filter ('LABEL', 'SELECT', 'NUMBER').
     * Defaults to 'LABEL'.
     * @param {Array} [options] Array of options (with 'id' and 'labelKey' properties).
     * @param {Function} [onChange] Callback called when the filter change.
     * @return {Filter} A filter.
     */
    static booleanFilter(key, placeholderKey, value, onChange) {
        return Filter.equalFilter(key, value, placeholderKey, undefined, 'SELECT', Filter.yesNoOptions(), onChange);
    }

    /**
     * Builds a filter on alarm status.
     *
     * @param {String} [value] Default selected value.
     * @return {Filter} A filter.
     */
    static alarmStatusFilter(value) {
        return Filter.equalFilter('status', value, 'model.alarm.status.status', 'ALARM_STATUS', 'SELECT', [{
            id: 'PENDING',
            labelKey: 'model.alarm.status.PENDING'
        }, {
            id: 'CLOSED',
            labelKey: 'model.alarm.status.CLOSED'
        }]);
    }

    /**
     * Builds a filter on action request status.
     *
     * @param {String} [value] Default selected value.
     * @return {Filter} A filter.
     */
    static actionRequestStatusFilter(value) {
        return Filter.equalFilter('status', value, 'model.actionrequest.status.status', 'ACTION_REQUEST_STATUS',
            'SELECT', [{
                id: 'OPENED',
                labelKey: 'model.actionrequest.status.OPENED'
            }, {
                id: 'COMPLETED',
                labelKey: 'model.actionrequest.status.COMPLETED'
            }, {
                id: 'CLOSED',
                labelKey: 'model.actionrequest.status.CLOSED'
            }, {
                id: 'CANCELED',
                labelKey: 'model.actionrequest.status.CANCELED'
            }]
        );
    }

    /**
     * Builds a filter on action request prog mes status.
     *
     * @param {String} [value] Default selected value.
     * @return {Filter} A filter.
     */
    static actionRequestProgMesStatusFilter(value) {
        return Filter.equalFilter('status', value, 'model.actionrequestprogmes.status.status', 'ACTION_REQUEST_STATUS',
            'SELECT', [{
                id: 'OPENED',
                labelKey: 'model.actionrequestprogmes.status.OPENED'
            }, {
                id: 'COMPLETED',
                labelKey: 'model.actionrequestprogmes.status.COMPLETED'
            }, {
                id: 'CLOSED',
                labelKey: 'model.actionrequestprogmes.status.CLOSED'
            }, {
                id: 'CANCELED',
                labelKey: 'model.actionrequestprogmes.status.CANCELED'
            }]
        );
    }

    /**
     * Builds a filter on preventive sheet status.
     *
     * @param {String} [value] Default selected value.
     * @return {Filter} A filter.
     */
    static preventiveSheetStatusFilter(value) {
        return Filter.equalFilter('status', value, 'model.preventivesheet.status.status', 'PREVENTIVE_SHEET_STATUS',
            'SELECT', [{
                id: 'OPENED',
                labelKey: 'model.preventivesheet.status.OPENED'
            }, {
                id: 'COMPLETED',
                labelKey: 'model.preventivesheet.status.COMPLETED'
            }]
        );
    }

    static yesNoOptions() {
        return [{id: true, labelKey: 'ui.button.yes'}, {id: false, labelKey: 'ui.button.no'}];
    }
}

export { Filter };



// WEBPACK FOOTER //
// ./src/main/front/scripts/model/Filter.js